import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import CableRoundedIcon from '@mui/icons-material/CableRounded';

import { devicesApi } from '../../api/devices';
import MeterRadioHeadPanel from './MeterRadioHeadPanel';
import PulseRadioModuleDialog from './PulseRadioModuleDialog';

const WATER_TYPES = new Set(['ХВС', 'ГВС']);

function apiError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return fallback;
}

function UspdBusPanel({ device, onChanged, onClose }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [current, setCurrent] = useState(null);
  const [options, setOptions] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [gatewayId, setGatewayId] = useState('');
  const [lineId, setLineId] = useState('');
  const [busAddress, setBusAddress] = useState('');
  const [profileKey, setProfileKey] = useState('');
  const [reason, setReason] = useState('');
  const [closeReason, setCloseReason] = useState('');

  const load = useCallback(async () => {
    if (!device?.id) return;
    setLoading(true);
    setError('');
    try {
      const [conn, uspd, availableProfiles] = await Promise.all([
        devicesApi.getBusConnection(device.id),
        devicesApi.getBusConnectionOptions(device.id),
        devicesApi.listBusProfiles(),
      ]);
      setCurrent(conn);
      setOptions(uspd || []);
      setProfiles(availableProfiles || []);
      if (conn?.is_bus) {
        setGatewayId(conn.gateway_id || '');
        setLineId(conn.line_id || '');
        setBusAddress(conn.bus_address || '');
        setProfileKey(conn.profile_key || '');
      }
    } catch (err) {
      setError(apiError(err, 'Не удалось загрузить подключение к УСПД'));
    } finally {
      setLoading(false);
    }
  }, [device?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedGateway = useMemo(
    () => options.find((item) => item.id === gatewayId) || null,
    [options, gatewayId],
  );
  const lines = useMemo(() => selectedGateway?.lines || [], [selectedGateway]);
  const selectedLine = useMemo(
    () => lines.find((item) => item.id === lineId) || null,
    [lines, lineId],
  );
  const compatibleProfiles = useMemo(
    () => profiles.filter((item) => item.transport === selectedLine?.transport_type),
    [profiles, selectedLine?.transport_type],
  );
  const selectedProfile = useMemo(
    () => profiles.find((item) => item.key === profileKey) || null,
    [profiles, profileKey],
  );
  const needsReason = Boolean(current && (
    current.is_pulse
    || current.line_id !== lineId
    || String(current.bus_address) !== String(busAddress)
    || (current.profile_key && current.profile_key !== profileKey)
  ));

  useEffect(() => {
    let cancelled = false;
    async function suggest() {
      if (!device?.id || !lineId || !selectedProfile) return;
      if (!selectedProfile.addressing_mode.endsWith('_primary')) return;
      if (current?.is_bus && current.line_id === lineId && busAddress) return;
      try {
        const next = await devicesApi.suggestBusAddress(device.id, lineId);
        if (!cancelled) setBusAddress(next);
      } catch {
        /* ignore */
      }
    }
    suggest();
    return () => { cancelled = true; };
  }, [device?.id, lineId, selectedProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedLine || !profiles.length) return;
    if (profiles.some((item) => item.key === profileKey && item.transport === selectedLine.transport_type)) return;
    const deviceLabel = `${device?.manufacturer || ''} ${device?.model || ''} ${device?.name || ''}`.toLowerCase();
    const inferred = (
      deviceLabel.includes('пульс стк')
      || deviceLabel.includes('пульс-стк')
      || deviceLabel.includes('puls stk')
      || deviceLabel.includes('puls-stk')
    )
      ? profiles.find((item) => item.key === 'puls_stk_rs485')
      : null;
    const lineProfile = profiles.find((item) => item.key === selectedLine.protocol);
    const fallback = profiles.find((item) => (
      item.transport === selectedLine.transport_type
      && item.addressing_mode.endsWith('_primary')
    ));
    const next = inferred || lineProfile || fallback || compatibleProfiles[0];
    setProfileKey(next?.key || '');
  }, [compatibleProfiles, device, profileKey, profiles, selectedLine]);

  const serialAddressSuggestion = useMemo(() => {
    if (selectedProfile?.address_suggestion !== 'serial_last_8') return '';
    const digits = String(device?.serial_number || '').replace(/\D/g, '');
    return digits.length >= 8 ? digits.slice(-8) : '';
  }, [device?.serial_number, selectedProfile?.address_suggestion]);

  const handleSave = async () => {
    if (!device?.id || !lineId || !busAddress.trim()) return;
    if (needsReason && !reason.trim()) {
      setError('Укажите причину смены подключения');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await devicesApi.upsertBusConnection(device.id, {
        line_id: lineId,
        transport_type: selectedLine?.transport_type === 'mbus' ? 'mbus' : 'rs485',
        bus_address: busAddress.trim(),
        profile_key: profileKey || undefined,
        reason: needsReason ? reason.trim() : undefined,
      });
      await load();
      onChanged?.();
    } catch (err) {
      setError(apiError(err, 'Не удалось повесить прибор на линию УСПД'));
    } finally {
      setSaving(false);
    }
  };

  const handleCloseBus = async () => {
    if (!device?.id || !closeReason.trim()) {
      setError('Укажите причину отключения');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await devicesApi.closeBusConnection(device.id, closeReason.trim());
      setCloseReason('');
      setReason('');
      await load();
      onChanged?.();
    } catch (err) {
      setError(apiError(err, 'Не удалось отключить от линии УСПД'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: 220, display: 'grid', placeItems: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Stack spacing={1.5}>
      {error ? <Alert severity="error" onClose={() => setError('')}>{error}</Alert> : null}
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        Прибор вешается на линию УСПД (RS485/M-Bus). Импульсный канал и шина взаимоисключающи —
        у прибора может быть только одно активное подключение. Линии должны уже существовать
        (например раскладка Пульсар в разделе «Агенты»).
      </Alert>

      {current ? (
        <Alert severity={current.is_bus ? 'success' : 'warning'} sx={{ borderRadius: 2 }}>
          Сейчас:
          {' '}
          {current.is_pulse
            ? `импульсный канал ${current.channel_number ?? current.bus_address}`
            : `${current.gateway_name}, линия ${current.line_number}, адрес ${current.bus_address}`}
        </Alert>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Активного подключения нет.
        </Typography>
      )}

      {!options.length ? (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          В доме нет УСПД с линиями RS485/M-Bus. Создайте УСПД и линии заранее.
        </Alert>
      ) : (
        <>
          <FormControl size="small" fullWidth>
            <InputLabel>УСПД</InputLabel>
            <Select
              label="УСПД"
              value={gatewayId}
              onChange={(event) => {
                setGatewayId(event.target.value);
                setLineId('');
                setBusAddress('');
                setProfileKey('');
              }}
            >
              {options.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}{item.host ? ` · ${item.host}` : ''}
                  {item.lines?.length ? ` · ${item.lines.length} лин.` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth disabled={!gatewayId}>
            <InputLabel>Линия</InputLabel>
            <Select
              label="Линия"
              value={lineId}
              onChange={(event) => {
                setLineId(event.target.value);
                setBusAddress('');
                setProfileKey('');
              }}
            >
              {lines.map((line) => (
                <MenuItem key={line.id} value={line.id}>
                  {line.line_number}
                  {line.name ? ` · ${line.name}` : ''}
                  {' · '}
                  {line.transport_type}
                  {' · '}
                  {line.device_count} приб.
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth disabled={!lineId}>
            <InputLabel>Профиль прибора</InputLabel>
            <Select
              label="Профиль прибора"
              value={profileKey}
              onChange={(event) => setProfileKey(event.target.value)}
            >
              {compatibleProfiles.map((profile) => (
                <MenuItem key={profile.key} value={profile.key}>
                  {profile.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
            <TextField
              size="small"
              label="Адрес на шине"
              value={busAddress}
              onChange={(event) => setBusAddress(event.target.value)}
              helperText={selectedProfile?.address_hint || 'Адрес прибора на выбранной линии'}
              disabled={!lineId}
              sx={{ flex: 1 }}
            />
            {serialAddressSuggestion ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() => setBusAddress(serialAddressSuggestion)}
                sx={{ mt: { sm: 0.25 }, whiteSpace: 'nowrap' }}
              >
                Подставить {serialAddressSuggestion}
              </Button>
            ) : null}
          </Stack>
          {needsReason ? (
            <TextField
              size="small"
              label="Причина смены подключения"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Перенос на УСПД, смена линии…"
              required
            />
          ) : null}
        </>
      )}

      <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
        <Button onClick={onClose} disabled={saving}>Закрыть</Button>
        {current?.is_bus ? (
          <>
            <TextField
              size="small"
              label="Причина отключения"
              value={closeReason}
              onChange={(event) => setCloseReason(event.target.value)}
              sx={{ minWidth: 220 }}
            />
            <Button color="warning" variant="outlined" disabled={saving || !closeReason.trim()} onClick={handleCloseBus}>
              Отключить от шины
            </Button>
          </>
        ) : null}
        <Button
          variant="contained"
          disabled={saving || !lineId || !profileKey || !busAddress.trim() || (needsReason && !reason.trim())}
          onClick={handleSave}
        >
          {saving ? 'Сохранение…' : 'Повесить на линию'}
        </Button>
      </Stack>
    </Stack>
  );
}

export default function DeviceConnectionHubDialog({ open, device, buildingId, onClose, onChanged }) {
  const [tab, setTab] = useState(0);
  const isWater = WATER_TYPES.has(String(device?.system_type || '').trim());

  useEffect(() => {
    if (open) setTab(0);
  }, [open, device?.id]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle sx={{ pb: 0.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CableRoundedIcon sx={{ color: '#2E73B8' }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900, color: '#1F3550', lineHeight: 1.15 }}>
              Подключение прибора
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B' }}>
              {device?.system_type || 'Прибор'} · {device?.serial_number || 'без серийного номера'}
            </Typography>
          </Box>
        </Stack>
        <Tabs
          value={tab}
          onChange={(_event, next) => setTab(next)}
          sx={{ mt: 1.5, minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 700 } }}
        >
          {isWater ? <Tab label="Накладка" /> : null}
          <Tab label="Импульс / регистратор" />
          <Tab label="Шина УСПД (RS485 / M-Bus)" />
        </Tabs>
      </DialogTitle>
      <DialogContent dividers sx={{ background: '#F7F9FC', pt: 2 }}>
        {isWater && tab === 0 ? (
          <MeterRadioHeadPanel
            embedded
            open={open && tab === 0}
            device={device}
            buildingId={buildingId}
            onClose={onClose}
            onChanged={onChanged}
          />
        ) : (isWater ? tab === 1 : tab === 0) ? (
          <PulseRadioModuleDialog
            embedded
            open={open && (isWater ? tab === 1 : tab === 0)}
            device={device}
            buildingId={buildingId}
            onClose={onClose}
            onChanged={onChanged}
          />
        ) : (
          <UspdBusPanel device={device} onChanged={onChanged} onClose={onClose} />
        )}
      </DialogContent>
    </Dialog>
  );
}
